/**
 * recordingDelivery (Welle 7e / pre96) — Zustellung des fertigen Aufzeichnungspakets.
 *
 * pre96 implementiert den `folder`-Adapter über die File System Access API (FI-Hot-Folder:
 * das Paket landet im konfigurierten zentralen Eingangsordner) und `download` als Fallback.
 * Eigener, isolierter Verzeichnis-Handle (separate IndexedDB) — unabhängig von den
 * Dokumenten-Zielen (fileTargets TargetKind). Server-Adapter (SFTP/S3/Archiv) folgen.
 */

import { saveWithDialog } from './fileTargets';
import { apiFetch } from './auth';
import type { DeliveryAdapterId } from './recordingProfiles';
import type { BuiltPackage } from './recordingPackage';

const DB = 'bamoa-recording';
const STORE = 'kv';
const KEY = 'dir';

export function dirPickerSupported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet<T>(key: string): Promise<T | null> {
  const db = await openDb();
  return new Promise((resolve) => {
    const r = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
    r.onsuccess = () => resolve((r.result as T) ?? null);
    r.onerror = () => resolve(null);
  });
}
async function idbPut(key: string, val: unknown): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function idbDel(key: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

async function ensurePermission(handle: any, interactive: boolean): Promise<boolean> {
  try {
    const opts = { mode: 'readwrite' as const };
    if ((await handle.queryPermission(opts)) === 'granted') return true;
    if (!interactive) return false;
    return (await handle.requestPermission(opts)) === 'granted';
  } catch { return false; }
}

/** Zielordner für Aufzeichnungen wählen (persistiert). */
export async function pickRecordingDir(): Promise<string | null> {
  if (!dirPickerSupported()) return null;
  const handle = await (window as any).showDirectoryPicker({ mode: 'readwrite', id: 'bamoa-recording' });
  await idbPut(KEY, handle);
  return handle.name ?? null;
}
export async function getRecordingDirName(): Promise<string | null> {
  const h = await idbGet<any>(KEY);
  return h?.name ?? null;
}
export async function clearRecordingDir(): Promise<void> { await idbDel(KEY); }

// ── Empfänger-Public-Key (OpenPGP) für die Verschlüsselung der Ablage ─────────
const PUBKEY = 'pubkey';
export async function getRecipientKey(): Promise<string | null> { return idbGet<string>(PUBKEY); }
export async function setRecipientKey(armored: string): Promise<void> { await idbPut(PUBKEY, armored); }
export async function clearRecipientKey(): Promise<void> { await idbDel(PUBKEY); }

export type RecAccess = 'none' | 'granted' | 'prompt' | 'denied';

/** Handle direkt holen (z. B. um requestPermission im selben Klick-Gesten-Kontext aufzurufen). */
export async function getRecordingHandle(): Promise<any | null> {
  return idbGet<any>(KEY);
}
/** Aktueller Berechtigungsstatus des Zielordners. */
export async function getRecordingAccess(): Promise<RecAccess> {
  const h = await idbGet<any>(KEY);
  if (!h) return 'none';
  try { return (await h.queryPermission({ mode: 'readwrite' })) as RecAccess; }
  catch { return 'prompt'; }
}
/** Berechtigung interaktiv anfragen — MUSS im Nutzer-Gesten-Kontext laufen. */
export async function requestRecordingAccess(handle?: any): Promise<RecAccess> {
  const h = handle ?? (await idbGet<any>(KEY));
  if (!h) return 'none';
  try { return (await h.requestPermission({ mode: 'readwrite' })) as RecAccess; }
  catch { return 'prompt'; }
}

export interface DeliveryResult {
  ok: boolean;
  method: DeliveryAdapterId;
  name?: string;
  reason?: string;
}

/**
 * Welle 12 (pre134): Empfangenes Paket DIREKT in den Aufzeichnungsordner schreiben — ohne
 * stillen Dialog-Fallback. `interactive:false` schreibt nur bei bereits erteilter Berechtigung;
 * `interactive:true` (aus einer Nutzer-Geste) fragt sie an. Für den Companion-Empfang.
 */
export async function writeRecordingToDir(fileName: string, blob: Blob, opts: { interactive?: boolean } = {}): Promise<'saved' | 'denied' | 'no-dir' | 'error'> {
  const handle = await idbGet<any>(KEY);
  if (!handle) return 'no-dir';
  if (!(await ensurePermission(handle, opts.interactive ?? false))) return 'denied';
  try {
    const fileHandle = await handle.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return 'saved';
  } catch (e) {
    console.warn('[rec-delivery] Direkt-Ablage fehlgeschlagen', e);
    return 'error';
  }
}

/**
 * Liefert das Paket gemäß Adapter aus.
 *  - 'folder'  : in den konfigurierten FSA-Ordner schreiben; ist keiner gesetzt/erlaubt,
 *                Fallback auf 'download' (Nutzer wählt den Ort, FI-Ordner als startIn gemerkt).
 *  - 'download': „Speichern unter …" / klassischer Download.
 */
export async function deliverRecording(adapter: DeliveryAdapterId, pkg: BuiltPackage, callId?: string): Promise<DeliveryResult> {
  if (adapter === 'download') {
    const r = await saveWithDialog(pkg.fileName, pkg.blob);
    return { ok: r !== 'cancelled', method: 'download', name: pkg.fileName, reason: r };
  }
  if (adapter === 'folder') {
    const handle = await idbGet<any>(KEY);
    if (handle && (await ensurePermission(handle, false))) {
      try {
        const fileHandle = await handle.getFileHandle(pkg.fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(pkg.blob);
        await writable.close();
        return { ok: true, method: 'folder', name: pkg.fileName };
      } catch (e) {
        console.warn('[rec-delivery] Ordner-Ablage fehlgeschlagen, Fallback Download', e);
      }
    }
    // Fallback: kein Ordner gesetzt / keine Berechtigung → Datei-Dialog
    const r = await saveWithDialog(pkg.fileName, pkg.blob);
    return { ok: r !== 'cancelled', method: 'folder', name: pkg.fileName, reason: handle ? 'no-permission→download' : 'no-dir→download' };
  }
  if (adapter === 'http-api') {
    // Server-Relay: NUR verschlüsseltes Paket hochladen (kein Klartext-Upload!).
    if (!pkg.fileName.toLowerCase().endsWith('.gpg')) {
      return { ok: false, method: 'http-api', reason: 'relay-requires-encryption' };
    }
    try {
      const dataB64 = await blobToBase64(pkg.blob);
      const res = await apiFetch('/api/recording/deliver', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callId: callId ?? '', fileName: pkg.fileName, rootHash: pkg.rootHash, profile: pkg.profile, dataB64 }),
      }, { noLogoutOn401: true } as any);
      return { ok: res.ok, method: 'http-api', name: pkg.fileName, reason: res.ok ? undefined : `http-${res.status}` };
    } catch {
      return { ok: false, method: 'http-api', reason: 'network' };
    }
  }
  // Übrige Server-Adapter folgen serverseitig.
  return { ok: false, method: adapter, reason: 'not-implemented' };
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) binary += String.fromCharCode(...bytes.subarray(i, i + step));
  return btoa(binary);
}

// ── Ablage-Protokoll je Call (für REC-Hinweis in der Nachbearbeitung) ─────────
export interface RecordingLogEntry {
  callId: string;
  fileName: string;
  ok: boolean;
  method: string;
  profile: string;
  rootHash: string;
  at: number;
}

export async function logRecordingDelivery(e: RecordingLogEntry): Promise<void> {
  try { await idbPut('log:' + e.callId, e); } catch { /* */ }
}

export async function getAllRecordingLogs(): Promise<Record<string, RecordingLogEntry>> {
  const out: Record<string, RecordingLogEntry> = {};
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) { resolve(); return; }
        if (typeof cur.key === 'string' && cur.key.startsWith('log:')) {
          const v = cur.value as RecordingLogEntry;
          if (v && v.callId) out[v.callId] = v;
        }
        cur.continue();
      };
      req.onerror = () => resolve();
    });
  } catch { /* */ }
  return out;
}
